import { useRef, useState } from 'react';
import type * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import type { SceneProps, LinkSceneProps } from '.';
import { deg2rad } from '../../../_utils/deg2rad';
import { roundedLerp } from '../../../_utils/roundedLerp';
import { roundedRotationLerp } from '../../../_utils/roundedRotationLerp';
import { round } from '../../../_utils/round';
import { Marker } from './Marker';
import type { ScrollSceneAnimationControls } from '../../../ThreeLayout/ScrollScene';
import { useSaveCustomCursor } from '../../../../CustomCursor';

type dockRefsType = {
	group: React.RefObject<THREE.Group>;
	dock: React.RefObject<THREE.Group>;
	hub: React.RefObject<THREE.Group>;
	headsetGroup: React.RefObject<THREE.Group>;
	headset1: React.RefObject<THREE.Mesh>;
	headset2: React.RefObject<THREE.Mesh>;
	headset3: React.RefObject<THREE.Mesh>;
};

export function DockWithHubModel(props: SceneProps & ScrollSceneAnimationControls) {
	const saveCustomCursor = useSaveCustomCursor();

	const dockRefs = useRef<dockRefsType>({
		group: useRef(null),
		dock: useRef(null),
		hub: useRef(null),
		headsetGroup: useRef(null),
		headset1: useRef(null),
		headset2: useRef(null),
		headset3: useRef(null),
	}).current;

	const [markerVisible, setMarkerVisible] = useState<boolean>(true);

	const hover = useRef({
		allowOut: true,
	}).current;

	useOnClickAnimation(dockRefs, props.modelsInfo);

	useFrame(() => {
		if (window) {
			/**
			 * If there is no active product allow hover events
			 */
			if (!props.modelsInfo.activeProduct) {
				if (props.sections.dockHover.current) {
					if (props.hover.hoverObject === 'dock') {
						if (props.sections.dockHover.current.style.pointerEvents !== 'auto') {
							props.sections.dockHover.current.style.pointerEvents = 'auto';
						}
					}
					if (props.hover.hoverObject !== 'dock') {
						if (props.sections.dockHover.current.style.pointerEvents !== 'none') {
							props.sections.dockHover.current.style.pointerEvents = 'none';
						}
					}
				}

				if (!markerVisible) setMarkerVisible(true);
			} else {
				/**
				 * reset hover if there is an active product
				 */
				if (props.sections.dockHover.current) {
					if (props.sections.dockHover.current.style.pointerEvents !== 'none') {
						props.sections.dockHover.current.style.pointerEvents = 'none';
					}
				}

				if (markerVisible) setMarkerVisible(false);
			}
		}
	});

	return (
		<>
			{props.overlines?.dock && (
				<Marker {...{ position: [3, 16.5, -4] }} style={{ opacity: markerVisible ? 1 : 0 }}>
					{props.overlines.dock}
					<div className={`h-8 origin-top-left mt-4`}>
						<div className={`w-px h-8 bg-linear-to-b from-coral-100 to-coral-100/0`} />
					</div>
				</Marker>
			)}
			<mesh
				scale={5}
				{...{ position: [7.5, 8.5, -4], renderOrder: 1 }}
				onPointerMove={event => {
					event.stopPropagation();
					if (props.hover.hoverObject !== 'dock' && !props.modelsInfo.activeProduct) {
						props.hover.hoverObject = 'dock';
						props.hover.rotation.x = 5;
						props.hover.rotation.y = -5;
						props.hover.scale = 1.05;
						hover.allowOut = false;
						setTimeout(() => {
							hover.allowOut = true;
							saveCustomCursor({
								size: 'large',
								children: (
									<>
										{props.learnMore?.learn}
										<br />
										{props.learnMore?.more}
									</>
								),
							});
						}, 100);
					}
				}}
				onPointerLeave={event => {
					event.stopPropagation();
					if (props.hover.hoverObject === 'dock' && hover.allowOut) {
						props.hover.hoverObject = 'none';
						props.hover.rotation.x = 0;
						props.hover.rotation.y = 0;
						props.hover.scale = 0;
						saveCustomCursor({ size: 'small', children: <></> });
					}
				}}
				onWheel={event => {
					event.stopPropagation();
					if (props.hover.hoverObject === 'dock' && hover.allowOut) {
						props.hover.hoverObject = 'none';
						props.hover.rotation.x = 0;
						props.hover.rotation.y = 0;
						props.hover.scale = 0;
						saveCustomCursor({ size: 'small', children: <></> });
					}
				}}
				onClick={event => {
					event.stopPropagation();
					if (!props.modelsInfo.activeProduct && props.sections.modal.current) {
						saveCustomCursor({ size: 'small', children: <></> });
						props.setNewProductByName('Dock');
						props.scrollSceneAnimationControls.setSmoothPositioning('waiting for start');
						setTimeout(() => {
							props.scrollSceneAnimationControls.setAllowScrollAnimation(false);
							props.scrollSceneAnimationControls.setNewTrackerWrapper(props.sections.modal);
						}, 100);
					}
				}}
			>
				<planeGeometry {...{ args: [4, 4] }} />
				<meshBasicMaterial {...{ color: 'white', transparent: true, opacity: 0 }} />
			</mesh>
			<group>
				{props.scene && (
					<>
						<group ref={dockRefs.group} {...{ position: [6.9, 0, -4], rotation: [0, deg2rad(-21), 0] }}>
							<group ref={dockRefs.dock}>
								<primitive {...{ object: props.scene.Dock }} />
							</group>
							<group ref={dockRefs.hub}>
								<primitive
									{...{
										object: props.scene.Hub,
									}}
								/>
							</group>
							<group ref={dockRefs.headsetGroup} {...{ position: [0, 9.6, -0.8], rotation: [deg2rad(65), 0, 0] }}>
								<mesh
									ref={dockRefs.headset1}
									{...{ position: [-7.5, 0, 0], geometry: props.scene.Headset.geometry, material: props.scene.HeadsetDockMaterialFirst }}
								/>
								<mesh
									ref={dockRefs.headset2}
									{...{ position: [2.5, 0, 0], geometry: props.scene.Headset.geometry, material: props.scene.HeadsetDockMaterialSecond }}
								/>
								<mesh
									ref={dockRefs.headset3}
									{...{ position: [7.5, 0, 0], geometry: props.scene.Headset.geometry, material: props.scene.HeadsetDockMaterialSecond }}
								/>
							</group>
						</group>
					</>
				)}
			</group>
		</>
	);
}

function useOnClickAnimation(dockRefs: dockRefsType, modelsInfo: LinkSceneProps['modelsInfo']) {
	const animation = useRef<{
		needReset: boolean;
		lastProductPosition: number | undefined;
		dockPrevPosition: number | undefined;
		hubPrevPosition: number | undefined;
		delay: number;
	}>({
		needReset: false,
		lastProductPosition: undefined,
		dockPrevPosition: undefined,
		hubPrevPosition: undefined,
		delay: 0,
	}).current;

	useFrame((state, d) => {
		if (window && dockRefs.group.current) {
			const orientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
			const ratio = orientation === 'portrait' ? (window.innerHeight / window.innerWidth) * 0.65 : 1;

			const delta = d / (!animation.lastProductPosition ? 1 : Math.abs(animation.lastProductPosition) + 0.1);

			/**
			 * if modal is open (when active product is set) we start positioning models on the screen depending on product position
			 */

			if (modelsInfo.activeProduct && modelsInfo.products) {
				/**
				 * setting default position. if product position is dock or hub we set it to 0. Setting the position to 1 if Dock has position is important
				 * for animation from page to modal, because in this case product will animate to the right side of the screen
				 */
				let productPosition =
					modelsInfo.products.Dock.position === 0 || modelsInfo.products.Hub.position === 0
						? 0
						: modelsInfo.products.Dock.position === 1
							? 1
							: -1;

				/**
				 * setting and saving products positions when it changes and only on change once or it start to recalculate during animation and broke the correct position
				 */
				if (
					animation.dockPrevPosition !== modelsInfo.products.Dock.position &&
					animation.hubPrevPosition !== modelsInfo.products.Hub.position
				) {
					/**
					 * recalculate position on change
					 */
					if (
						animation.lastProductPosition !== undefined &&
						animation.dockPrevPosition !== undefined &&
						animation.hubPrevPosition !== undefined
					) {
						if (modelsInfo.products.Hub.position === 0) {
							productPosition = 0;
							if (animation.hubPrevPosition < 0 && animation.lastProductPosition !== 0) {
								dockRefs.group.current.position.x = dockActiveGroupProps[orientation].x + -1 * 105;
							}
							if (animation.hubPrevPosition > 0 && animation.lastProductPosition !== 0) {
								dockRefs.group.current.position.x = dockActiveGroupProps[orientation].x + 1 * 105;
							}
						}

						if (modelsInfo.products.Dock.position === 0) {
							productPosition = 0;
							if (animation.dockPrevPosition < 0 && animation.lastProductPosition !== 0) {
								dockRefs.group.current.position.x = dockActiveGroupProps[orientation].x + -1 * 105;
							}
							if (animation.dockPrevPosition > 0 && animation.lastProductPosition !== 0) {
								dockRefs.group.current.position.x = dockActiveGroupProps[orientation].x + 1 * 105;
							}
						}

						if (animation.lastProductPosition === 0 && modelsInfo.products.Hub.position !== 0 && modelsInfo.products.Dock.position !== 0) {
							if (modelsInfo.products.Hub.position < 0 && animation.hubPrevPosition === 0) {
								productPosition = -1;
							}
							if (modelsInfo.products.Dock.position > 0 && animation.dockPrevPosition === 0) {
								productPosition = 1;
							}
						}
					}

					if (modelsInfo.activeProduct === 'Hub' && animation.hubPrevPosition === -1) {
						animation.delay = 0.8;
					}

					animation.lastProductPosition = productPosition;
					animation.dockPrevPosition = modelsInfo.products.Dock.position;
					animation.hubPrevPosition = modelsInfo.products.Hub.position;

					animation.needReset = true;
				} else {
					/**
					 * if product doesn't change position we use the last saved position
					 */
					productPosition = animation.lastProductPosition ? animation.lastProductPosition : productPosition;
				}

				if (animation.delay > 0) {
					animation.delay -= d;
				} else {
					animation.delay = 0;
				}

				/**
				 * scene group scale animation
				 */
				const scaleX = roundedLerp(
					dockRefs.group.current.scale.x,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].scale
						: dockActiveGroupProps[orientation].scale,
					delta,
					5,
				);
				if (scaleX !== dockRefs.group.current.scale.x) {
					dockRefs.group.current.scale.set(scaleX, scaleX, scaleX);
				}
				/**
				 * scene group rotation animation
				 */
				const rotationX = roundedRotationLerp(
					dockRefs.group.current.rotation.x,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].rotationX
						: dockActiveGroupProps[orientation].rotationX,
					delta,
					5,
				);
				if (rotationX !== dockRefs.group.current.rotation.x) {
					dockRefs.group.current.rotation.x = rotationX;
				}
				const rotationY = roundedRotationLerp(
					dockRefs.group.current.rotation.y,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].rotationY
						: dockActiveGroupProps[orientation].rotationY,
					delta,
					5,
				);
				if (rotationY !== dockRefs.group.current.rotation.y) {
					dockRefs.group.current.rotation.y = rotationY;
				}
				const rotationZ = roundedRotationLerp(
					dockRefs.group.current.rotation.z,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].rotationZ
						: dockActiveGroupProps[orientation].rotationZ,
					delta,
					5,
				);
				if (rotationZ !== dockRefs.group.current.rotation.z) {
					dockRefs.group.current.rotation.z = rotationZ;
				}

				/**
				 * scene group position animation
				 */

				const positionX = roundedLerp(
					dockRefs.group.current.position.x,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].x
						: dockActiveGroupProps[orientation].x + productPosition * 105,
					delta,
					5,
				);
				if (positionX !== dockRefs.group.current.position.x) {
					dockRefs.group.current.position.x = positionX;
				} else {
					if (productPosition !== 0) {
						dockRefs.group.current.visible = false;
					}
				}

				if (productPosition === 0) {
					dockRefs.group.current.visible = true;
				} else {
					const offscreenPosition = productPosition * 95;
					if (offscreenPosition < 0 && Math.abs(offscreenPosition) < Math.abs(dockRefs.group.current.position.x))
						dockRefs.group.current.visible = false;
					if (offscreenPosition > 0 && offscreenPosition < dockRefs.group.current.position.x + 27) dockRefs.group.current.visible = false;
				}

				const positionY = roundedLerp(
					dockRefs.group.current.position.y,
					modelsInfo.activeProduct === 'Hub' && animation.delay === 0
						? hubActiveGroupProps[orientation].y * ratio
						: dockActiveGroupProps[orientation].y * ratio,
					delta,
					5,
				);
				if (positionY !== dockRefs.group.current.position.y) {
					dockRefs.group.current.position.y = positionY;
				}

				/**
				 * dock position
				 */

				if (modelsInfo.activeProduct === 'Hub' && animation.delay === 0) {
					const delta = d / 2;
					const yOffset = 60 * (state.size.height / state.size.width);

					if (dockRefs.dock.current) {
						const y = roundedLerp(dockRefs.dock.current.position.y, yOffset, delta);
						if (dockRefs.dock.current.position.y !== y) dockRefs.dock.current.position.y = y;
					}
					if (dockRefs.headsetGroup.current) {
						const y = roundedLerp(dockRefs.headsetGroup.current.position.y, yOffset + 9.6, delta);
						if (dockRefs.headsetGroup.current.position.y !== y) dockRefs.headsetGroup.current.position.y = y;
					}
				} else {
					if (dockRefs.dock.current) {
						const y = roundedLerp(dockRefs.dock.current.position.y, 0, delta);
						if (dockRefs.dock.current.position.y !== y) dockRefs.dock.current.position.y = y;
					}
					if (dockRefs.headsetGroup.current) {
						const y = roundedLerp(dockRefs.headsetGroup.current.position.y, 9.6, delta);
						if (dockRefs.headsetGroup.current.position.y !== y) dockRefs.headsetGroup.current.position.y = y;
					}
				}
			}

			if (animation.needReset && !modelsInfo.activeProduct) {
				dockRefs.group.current.visible = true;

				/**
				 * scene group scale reset
				 */
				const scale = roundedLerp(dockRefs.group.current.scale.x, 1, delta, 5);
				if (scale !== dockRefs.group.current.scale.x) {
					dockRefs.group.current.scale.set(scale, scale, scale);
				}

				/**
				 * scene group rotation reset
				 */
				const rotationX = roundedLerp(dockRefs.group.current.rotation.x, 0, delta, 5);
				if (rotationX !== dockRefs.group.current.rotation.x) {
					dockRefs.group.current.rotation.x = rotationX;
				}
				const rotationY = roundedRotationLerp(dockRefs.group.current.rotation.y, -21, delta, 5);
				if (rotationY !== dockRefs.group.current.rotation.y) {
					dockRefs.group.current.rotation.y = rotationY;
				}
				const rotationZ = roundedRotationLerp(dockRefs.group.current.rotation.z, 0, delta, 5);
				if (rotationZ !== dockRefs.group.current.rotation.z) {
					dockRefs.group.current.rotation.z = rotationZ;
				}

				/**
				 * scene group position reset
				 */

				const positionX = roundedLerp(dockRefs.group.current.position.x, 6.9, delta, 5);
				if (positionX !== dockRefs.group.current.position.x) {
					dockRefs.group.current.position.x = positionX;
				}

				const positionY = roundedLerp(dockRefs.group.current.position.y, 0, delta, 5);
				if (positionY !== dockRefs.group.current.position.y) {
					dockRefs.group.current.position.y = positionY;
				}

				if (dockRefs.dock.current) {
					const y = roundedLerp(dockRefs.dock.current.position.y, 0, delta);
					if (dockRefs.dock.current.position.y !== y) dockRefs.dock.current.position.y = y;
				}
				if (dockRefs.headsetGroup.current) {
					const y = roundedLerp(dockRefs.headsetGroup.current.position.y, 9.6, delta);
					if (dockRefs.headsetGroup.current.position.y !== y) dockRefs.headsetGroup.current.position.y = y;
				}

				if (
					round(dockRefs.group.current.scale.x, 2) === 1 &&
					round(dockRefs.group.current.position.x, 1) === 6.9 &&
					round(dockRefs.group.current.position.y, 2) === 0 &&
					round(dockRefs.group.current.position.z, 2) === 0 &&
					round(dockRefs.group.current.rotation.y, 2) === deg2rad(-21) &&
					round(dockRefs.group.current.rotation.x, 2) === 0
				) {
					dockRefs.group.current.scale.set(1, 1, 1);
					dockRefs.group.current.rotation.set(0, 0, 0);
					dockRefs.group.current.position.set(0, 0, 0);
					animation.needReset = false;
					animation.lastProductPosition = undefined;
					animation.dockPrevPosition = undefined;
					animation.hubPrevPosition = undefined;
				}
			}
		}
	});
}

const dockActiveGroupProps = {
	landscape: { x: -12, y: 4, rotationX: 0, rotationY: 23, rotationZ: 0, scale: 0.85 },
	portrait: { x: -1, y: 15, rotationX: 15, rotationY: 0, rotationZ: 0, scale: 1.3 },
};

const hubActiveGroupProps = {
	landscape: { x: -11.4, y: 2, rotationX: 30, rotationY: 23, rotationZ: -12, scale: 1.2 },
	portrait: { x: -1, y: 14, rotationX: 38, rotationY: 4, rotationZ: -2.5, scale: 1.7 },
};
